import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  ArrowLeft, CheckCircle2, Circle, FileText, Film, ClipboardList, Download, Upload,
  Loader2, Star, BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * ClientCourseDetail — student lesson player + worksheet runner.
 * - Lessons listed left; selected lesson shown right with video + body
 *   text + any trainer-uploaded attachment.
 * - "Mark complete" updates the enrollment's `completed_lesson_ids`
 *   and re-renders the progress bar immediately.
 * - Worksheets:
 *   • fillable → inline form with debounced auto-save to
 *     `/my/worksheet-drafts/{id}` (so refresh never loses typed answers).
 *     Submit posts to the existing admin/trainer-visible submissions
 *     endpoint and locks the UI as "submitted".
 *   • pdf → downloadable prompt + upload-your-completed-pdf flow via
 *     the shared `/files/upload` endpoint then submits the file URL.
 */
export default function ClientCourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/my/courses/${id}`);
      setData(data);
      if (!activeLessonId && data.lessons?.[0]) setActiveLessonId(data.lessons[0].id);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('You are not enrolled in this course.');
        navigate('/client/courses');
      } else {
        toast.error('Failed to load course');
      }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const activeLesson = useMemo(
    () => (data?.lessons || []).find(l => l.id === activeLessonId) || null,
    [data, activeLessonId],
  );

  const completedSet = useMemo(() => new Set(data?.completed_lesson_ids || []), [data]);
  const total = data?.lessons?.length || 0;
  const done = Math.min(completedSet.size, total);
  const pct = total ? Math.round((done / total) * 100) : 0;

  const toggleLessonComplete = async (lesson) => {
    const isDone = completedSet.has(lesson.id);
    try {
      if (isDone) {
        await api.post(`/my/courses/${id}/lessons/${lesson.id}/incomplete`);
      } else {
        await api.post(`/my/courses/${id}/lessons/${lesson.id}/complete`);
        toast.success('Lesson marked complete');
      }
      load();
    } catch { toast.error('Could not update progress'); }
  };

  if (!data) {
    return <div className='p-6 text-sm text-slate-500 dark:text-slate-400'>Loading…</div>;
  }

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto' data-testid='client-course-detail-page'>
      <button
        className='text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 mb-3 inline-flex items-center gap-1'
        onClick={() => navigate('/client/courses')}
        data-testid='client-course-back'
      >
        <ArrowLeft className='w-3.5 h-3.5' /> Back to my courses
      </button>

      <div className='mb-6'>
        <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Course</div>
        <h1 className='font-display text-3xl font-bold text-slate-900 dark:text-slate-50 mt-1'>{data.course.title}</h1>
        {data.course.description && (
          <p className='text-slate-500 dark:text-slate-400 text-sm mt-1 max-w-3xl'>{data.course.description}</p>
        )}
        {data.is_preview && (
          <Badge variant='outline' className='mt-2 border-[#B45309] text-[#B45309]'>Admin preview — progress won’t save</Badge>
        )}

        <div className='flex items-center gap-3 mt-4 max-w-md'>
          <Progress value={pct} className='h-2 flex-1' data-testid='client-course-progress-bar' />
          <div className='text-xs font-semibold text-slate-700 dark:text-slate-200 tabular-nums'>{done}/{total} · {pct}%</div>
        </div>
      </div>

      <div className='grid lg:grid-cols-[280px_1fr] gap-5'>
        {/* Lesson list */}
        <Card className='p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 h-max'>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400 px-2 pt-1 pb-2 flex items-center gap-1'>
            <BookOpen className='w-3 h-3' /> Lessons
          </div>
          {data.lessons.length === 0 ? (
            <div className='text-xs text-slate-500 px-2 py-4'>No lessons yet.</div>
          ) : (
            <ul className='space-y-1'>
              {data.lessons.map((l, i) => {
                const isDone = completedSet.has(l.id);
                const isActive = l.id === activeLessonId;
                return (
                  <li key={l.id}>
                    <button
                      onClick={() => setActiveLessonId(l.id)}
                      className={`w-full text-left flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors ${
                        isActive
                          ? 'bg-[#1B3A87]/10 text-[#1B3A87] dark:bg-[#1B3A87]/30 dark:text-white font-semibold'
                          : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                      data-testid={`client-lesson-item-${l.id}`}
                    >
                      {isDone ? (
                        <CheckCircle2 className='w-4 h-4 text-[#167A5A] shrink-0' />
                      ) : (
                        <Circle className='w-4 h-4 text-slate-300 shrink-0' />
                      )}
                      <span className='flex-1 truncate'>{i + 1}. {l.title}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Lesson content + worksheets */}
        <div className='space-y-5'>
          {activeLesson && (
            <Card className='p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800' data-testid='client-active-lesson'>
              <div className='flex items-start justify-between gap-3 mb-3 flex-wrap'>
                <div>
                  <h2 className='font-display text-xl font-bold text-slate-900 dark:text-slate-50'>{activeLesson.title}</h2>
                </div>
                <Button
                  size='sm'
                  onClick={() => toggleLessonComplete(activeLesson)}
                  className={completedSet.has(activeLesson.id)
                    ? 'bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100'
                    : 'bg-[#167A5A] hover:bg-[#126148] text-white'}
                  data-testid='client-lesson-toggle-complete'
                >
                  {completedSet.has(activeLesson.id) ? (
                    <>Mark incomplete</>
                  ) : (
                    <><CheckCircle2 className='w-4 h-4 mr-1' /> Mark complete</>
                  )}
                </Button>
              </div>

              {activeLesson.video_url && (
                <div className='mb-4 rounded-lg overflow-hidden bg-slate-900 aspect-video'>
                  <video
                    src={activeLesson.video_url}
                    controls
                    className='w-full h-full'
                    data-testid='client-lesson-video'
                  />
                </div>
              )}

              {activeLesson.body && (
                <div className='text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed mb-3'>
                  {activeLesson.body}
                </div>
              )}

              {activeLesson.attachment_url && (
                <a
                  href={activeLesson.attachment_url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex items-center gap-2 text-sm text-[#1B3A87] dark:text-sky-300 font-semibold hover:underline'
                  data-testid='client-lesson-attachment'
                >
                  <FileText className='w-4 h-4' />
                  {activeLesson.attachment_name || 'Download lesson attachment'}
                </a>
              )}

              {!activeLesson.video_url && !activeLesson.body && !activeLesson.attachment_url && (
                <div className='text-sm text-slate-500 dark:text-slate-400'>
                  <Film className='w-4 h-4 inline mr-1 text-slate-400' /> Your trainer hasn’t added lesson content yet.
                </div>
              )}
            </Card>
          )}

          {/* Worksheets */}
          {data.worksheets.length > 0 && (
            <div>
              <h3 className='font-display text-lg font-semibold text-slate-900 dark:text-slate-50 mb-3 flex items-center gap-2'>
                <ClipboardList className='w-4 h-4 text-[#C8102E]' /> Worksheets
              </h3>
              <div className='space-y-4'>
                {data.worksheets.map(ws => (
                  <WorksheetRunner
                    key={ws.id}
                    courseId={id}
                    worksheet={ws}
                    submission={(data.submissions || []).find(s => s.worksheet_id === ws.id)}
                    onSubmitted={load}
                    isPreview={data.is_preview}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- WorksheetRunner ---------- */
const WorksheetRunner = ({ courseId, worksheet, submission, onSubmitted, isPreview }) => {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null); // {url, original_filename}
  const saveTimer = useRef(null);
  const [savedAt, setSavedAt] = useState(null);

  // Load existing draft on mount (for fillable worksheets only).
  useEffect(() => {
    if (worksheet.kind !== 'fillable' || submission) return;
    api.get(`/my/worksheet-drafts/${worksheet.id}`).then(r => {
      setAnswers(r.data?.answers || {});
    }).catch(() => {});
    // eslint-disable-next-line
  }, [worksheet.id]);

  // Debounced auto-save (fillable only).
  useEffect(() => {
    if (worksheet.kind !== 'fillable' || submission || isPreview) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.post(`/my/worksheet-drafts/${worksheet.id}`, { answers })
        .then(() => setSavedAt(new Date()))
        .catch(() => {});
    }, 700);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line
  }, [answers]);

  const setQ = (qid, val) => setAnswers(a => ({ ...a, [qid]: val }));

  const submitFillable = async () => {
    // Soft validation for required text fields.
    const missing = (worksheet.questions || []).filter(q => q.required && !(String(answers[q.id] || '').trim()));
    if (missing.length > 0) { toast.error('Please answer required questions'); return; }
    setSubmitting(true);
    try {
      await api.post(`/courses/${courseId}/worksheets/${worksheet.id}/submit`, { answers });
      toast.success('Worksheet submitted');
      onSubmitted();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
    finally { setSubmitting(false); }
  };

  const onPdfPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'client_media');
      const { data: up } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadedFile({ url: up.url, original_filename: up.original_filename || file.name });
      toast.success('File ready to submit');
    } catch { toast.error('Upload failed'); }
    finally { setUploadBusy(false); }
  };

  const submitPdf = async () => {
    if (!uploadedFile?.url) { toast.error('Upload your completed file first'); return; }
    setSubmitting(true);
    try {
      await api.post(`/courses/${courseId}/worksheets/${worksheet.id}/submit`, {
        file_url: uploadedFile.url,
        file_name: uploadedFile.original_filename,
      });
      toast.success('Worksheet submitted');
      onSubmitted();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
    finally { setSubmitting(false); }
  };

  const alreadySubmitted = !!submission;

  return (
    <Card
      className='p-4 sm:p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800'
      data-testid={`client-worksheet-${worksheet.id}`}
    >
      <div className='flex items-start justify-between flex-wrap gap-2 mb-3'>
        <div>
          <div className='font-semibold text-slate-900 dark:text-slate-50'>{worksheet.title}</div>
          <Badge variant='outline' className='mt-1 text-[10px]'>{worksheet.kind === 'pdf' ? 'Printable PDF' : 'Fillable form'}</Badge>
        </div>
        {alreadySubmitted ? (
          <Badge className='bg-[#167A5A] text-white'><CheckCircle2 className='w-3 h-3 mr-1' /> Submitted</Badge>
        ) : savedAt ? (
          <span className='text-[11px] text-slate-500'>Draft saved · {savedAt.toLocaleTimeString()}</span>
        ) : null}
      </div>

      {alreadySubmitted ? (
        <div className='text-xs text-slate-500 dark:text-slate-400'>
          Submitted {new Date(submission.created_at).toLocaleString()}. Your trainer will review your answers.
        </div>
      ) : worksheet.kind === 'pdf' ? (
        <div className='space-y-3'>
          {worksheet.pdf_url && (
            <a
              href={worksheet.pdf_url}
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center gap-2 text-sm text-[#1B3A87] dark:text-sky-300 font-semibold hover:underline'
              data-testid={`client-worksheet-pdf-${worksheet.id}`}
            >
              <Download className='w-4 h-4' /> Download worksheet PDF
            </a>
          )}
          <div className='text-xs text-slate-500 dark:text-slate-400'>
            Print it, complete it, then upload a photo or scan here:
          </div>
          <label className='inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 text-xs text-slate-700 dark:text-slate-200'>
            <Upload className='w-3.5 h-3.5' />
            {uploadBusy ? 'Uploading…' : (uploadedFile?.original_filename || 'Choose completed file')}
            <input
              type='file'
              accept='.pdf,image/*'
              className='hidden'
              onChange={onPdfPick}
              disabled={uploadBusy || isPreview}
              data-testid={`client-worksheet-upload-${worksheet.id}`}
            />
          </label>
          <div>
            <Button
              size='sm'
              onClick={submitPdf}
              disabled={!uploadedFile?.url || submitting || isPreview}
              className='bg-[#C8102E] hover:bg-[#A50D26] text-white'
              data-testid={`client-worksheet-submit-${worksheet.id}`}
            >
              {submitting && <Loader2 className='w-3.5 h-3.5 mr-1 animate-spin' />}
              Submit worksheet
            </Button>
          </div>
        </div>
      ) : (
        <div className='space-y-3'>
          {(worksheet.questions || []).map((q, i) => (
            <div key={q.id || i}>
              <label className='block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1'>
                {i + 1}. {q.label} {q.required && <span className='text-[#C8102E]'>*</span>}
              </label>
              {q.type === 'checkbox' ? (
                <label className='inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200'>
                  <input
                    type='checkbox'
                    checked={!!answers[q.id]}
                    onChange={e => setQ(q.id, e.target.checked)}
                    disabled={isPreview}
                    data-testid={`client-ws-${worksheet.id}-q-${q.id}`}
                  />
                  Yes
                </label>
              ) : q.type === 'rating' ? (
                <div className='flex items-center gap-1' data-testid={`client-ws-${worksheet.id}-q-${q.id}`}>
                  {[1,2,3,4,5].map(v => (
                    <button
                      key={v}
                      type='button'
                      onClick={() => setQ(q.id, v)}
                      disabled={isPreview}
                      className={(answers[q.id] >= v)
                        ? 'text-[#B08D57]'
                        : 'text-slate-300 dark:text-slate-600 hover:text-[#B08D57]'}
                      aria-label={`Rating ${v}`}
                    >
                      <Star className='w-5 h-5 fill-current' />
                    </button>
                  ))}
                </div>
              ) : (
                <Textarea
                  rows={2}
                  value={answers[q.id] || ''}
                  onChange={e => setQ(q.id, e.target.value)}
                  placeholder='Your answer…'
                  disabled={isPreview}
                  data-testid={`client-ws-${worksheet.id}-q-${q.id}`}
                />
              )}
            </div>
          ))}
          <div className='flex items-center gap-2 pt-1'>
            <Button
              size='sm'
              onClick={submitFillable}
              disabled={submitting || isPreview}
              className='bg-[#C8102E] hover:bg-[#A50D26] text-white'
              data-testid={`client-worksheet-submit-${worksheet.id}`}
            >
              {submitting && <Loader2 className='w-3.5 h-3.5 mr-1 animate-spin' />}
              Submit worksheet
            </Button>
            {isPreview && <span className='text-[11px] text-[#B45309]'>Preview mode — submissions disabled</span>}
          </div>
        </div>
      )}
    </Card>
  );
};
